import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useBlogContext } from '../BlogProvider';
import { BlogLangType } from '../../../types/blogTypes';
import {
    BlogPostListItemResponse,
    BlogPostListResponse,
} from '../../../server/response/blogResponseScheme';
import { blogApiClient } from '../../clientUtils/blogApiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import List from '../../uikit/List';
import { BlogCategoryBadge } from './BlogCategory';
import { Link, useNavigate } from 'react-router';
import _ from 'lodash';
import { useBlogTrans } from '../../blogHooks/useBlogTrans';

class ViewStore {
    readonly lang: BlogLangType | undefined;
    readonly category: string | undefined;
    readonly postHash: string | undefined;
    readonly pageSize: number | undefined;

    postList: BlogPostListResponse | null = null;
    lastHash: string | null = null;
    isLoading: boolean = false;

    constructor(lang: BlogLangType, category?: string, postHash?: string, pageSize?: number) {
        makeAutoObservable(this);

        this.lang = lang;
        this.category = category;
        this.postHash = postHash;
        this.pageSize = pageSize;
    }

    get isValid() {
        return !!this.lang;
    }

    async fetch() {
        if (!this.isValid || this.isLoading) {
            return;
        }

        this.isLoading = true;
        const postList = await blogApiClient.blogPost.getBlogPostList.$query({
            lang: this.lang!,
            category: this.category,
            fromHash: this.postHash,
            pageSize: this.pageSize,
        });

        runInAction(() => {
            this.postList = postList;
            this.lastHash = _.last(this.postList.posts)?.hash || null;
            this.isLoading = false;
        });
    }

    async fetchMore() {
        if (!this.isValid || this.isLoading) {
            return;
        }

        if (this.lastHash === null || this.postList?.isEnd) {
            return;
        }

        this.isLoading = true;

        const morePostList = await blogApiClient.blogPost.getBlogPostList.$query({
            lang: this.lang!,
            category: this.category,
            fromHash: this.lastHash,
        });

        runInAction(() => {
            const prevPosts = this.postList?.posts || [];
            this.postList = {
                ...morePostList,
                posts: [...prevPosts, ...morePostList.posts],
            };
            this.lastHash = _.last(this.postList.posts)?.hash || null;
            this.isLoading = false;
        });
    }
}

const BlogPostListItem = observer(({ post }: { post: BlogPostListItemResponse }) => {
    const { lang } = useBlogContext();
    const navigate = useNavigate();

    return (
        <List.Item key={post.hash}>
            <article
                className="list-col-grow flex cursor-pointer flex-col gap-3 py-2"
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/${lang}/post/${post.category}/${post.slug}`);
                }}
            >
                <div className="flex flex-row justify-between gap-4">
                    <div className="grow">
                        <header>
                            <h2 className="line-clamp-2 text-lg font-semibold">
                                <Link
                                    to={`/${lang}/post/${post.category}/${post.slug}`}
                                    className="link link-hover"
                                    preventScrollReset={false}
                                >
                                    {post.title}
                                </Link>
                            </h2>
                        </header>
                        <p className="mt-2 line-clamp-3">{post.seoDescription}</p>
                    </div>
                    {_.first(post.images) && (
                        <div className="h-24 w-32 shrink-0 overflow-hidden rounded md:h-32 md:w-48">
                            <img
                                src={_.first(post.images)}
                                alt={post.title}
                                className="h-full w-full object-cover"
                            />
                        </div>
                    )}
                </div>
                <footer className="flex flex-row items-center gap-2 text-sm">
                    <BlogCategoryBadge category={post.category} />
                    <time dateTime={post.publishedAt} className="text-base-content/70 text-sm">
                        {dayjs(post.publishedAt).format('YYYY-MM-DD HH:mm')}
                    </time>
                </footer>
            </article>
        </List.Item>
    );
});

const BlogPostList = observer(
    ({
        postHash,
        pageSize,
        category: categoryProps,
        displayCount,
    }: {
        /**
         * 관련 포스트 해시
         */
        postHash?: string;
        pageSize?: number;
        /**
         * 카테고리
         * 카테고리가 지정되면 카테고리 파라미터를 사용하고, 지정되지 않으면 블로그 컨텍스트의 카테고리를 사용함
         */
        category?: string;
        /**
         * 표시할 포스트 개수
         * 표시할 포스트 개수가 지정되면 더보기 버튼이 표시되지 않음
         */
        displayCount?: number;
    }) => {
        const { lang, category } = useBlogContext();
        const categoryParam = categoryProps || category;

        const { trans } = useBlogTrans();

        const viewStore = useMobxInstance(ViewStore, lang!, categoryParam, postHash, pageSize);

        useEffect(() => {
            viewStore.fetch();
        }, [viewStore]);

        if (!viewStore.isValid) {
            return null;
        }

        const displayPostList = _.isNumber(displayCount)
            ? _.take(viewStore.postList?.posts, displayCount)
            : viewStore.postList?.posts;

        return (
            <section className="flex flex-col gap-2">
                <List>
                    {displayPostList?.map((post) => (
                        <BlogPostListItem key={post.hash} post={post} />
                    ))}
                </List>
                {!displayCount && (
                    <footer>
                        {viewStore.postList?.isEnd ? null : (
                            <button
                                className="btn btn-block btn-soft font-normal"
                                onClick={() => viewStore.fetchMore()}
                            >
                                {viewStore.isLoading ? (
                                    <span className="loading loading-ring loading-sm"></span>
                                ) : (
                                    trans('more')
                                )}
                            </button>
                        )}
                    </footer>
                )}
            </section>
        );
    },
);

export default BlogPostList;
